// Intelligent Zen Mode — per-part configuration.
// ZEN_WORDS_PER_MINUTE is duplicated in backend/src/services/zenService.ts — keep in sync.

export const ZEN_WORDS_PER_MINUTE = 110;

export const ZEN_SLIDER_STEP = 0.5;

export interface ZenPartRange {
  min: number;   // default range start (minutes)
  max: number;   // default range end (minutes)
  cap: number;   // slider maximum (minutes)
}

const SHORT_PART: ZenPartRange = { min: 1.5, max: 2.5, cap: 15 };
const NORMAL_PART: ZenPartRange = { min: 3, max: 5, cap: 15 };

export const ZEN_PART_DEFAULT_RANGES: Record<string, ZenPartRange> = {
  introduction: SHORT_PART,
  awakening: SHORT_PART,
  reflection: SHORT_PART,
  selfguided: { min: 5, max: 10, cap: 60 },
  relaxation: NORMAL_PART,
  focus: NORMAL_PART,
  visualization: NORMAL_PART,
  manifestation: NORMAL_PART,
  affirmations: NORMAL_PART,
  remembrance: NORMAL_PART,
  healing: NORMAL_PART,
  suggestions: NORMAL_PART
};

export function getZenPartRange(partId: string): ZenPartRange {
  return ZEN_PART_DEFAULT_RANGES[partId] || NORMAL_PART;
}

export interface ZenPopularOption {
  key: string;
  label: string; // sent verbatim to the AI
}

export const ZEN_PART_POPULAR_OPTIONS: Record<string, ZenPopularOption[]> = {
  introduction: [
    { key: 'posture_guidance', label: 'Posture guidance (sitting or lying down)' },
    { key: 'eyes_close', label: 'Invite eyes to close' },
    { key: 'set_intention', label: 'Set an intention' },
    { key: 'three_breaths', label: 'Three deep breaths' },
    { key: 'welcome_purpose', label: 'Welcome & state the purpose' }
  ],
  relaxation: [
    { key: 'body_scan', label: 'Body scan' },
    { key: 'focus_breath', label: 'Focus on breath' },
    { key: 'counting_ten', label: 'Counting breaths to 10' },
    { key: 'progressive_muscle', label: 'Progressive muscle relaxation' },
    { key: 'heaviness_warmth', label: 'Heaviness & warmth suggestions' }
  ],
  focus: [
    { key: 'breath_anchor', label: 'Breath as anchor' },
    { key: 'counting_meditation', label: 'Counting meditation' },
    { key: 'noting_returning', label: 'Noting wandering thoughts & returning' },
    { key: 'body_anchor', label: 'Body-sensation anchor' },
    { key: 'sound_awareness', label: 'Sound awareness' }
  ],
  visualization: [
    { key: 'nature_scene', label: 'Nature scene (beach / forest / river)' },
    { key: 'safe_place', label: 'Safe-place imagery' },
    { key: 'descending_stairs', label: 'Descending-stairs deepener' },
    { key: 'healing_light', label: 'Healing light imagery' },
    { key: 'floating', label: 'Floating / weightlessness' }
  ],
  selfguided: [],
  manifestation: [
    { key: 'future_self', label: 'Future-self visualization' },
    { key: 'goal_achieved', label: 'Goal as already achieved' },
    { key: 'feel_success', label: 'Feel the emotions of success' },
    { key: 'mental_rehearsal', label: 'Mental rehearsal' },
    { key: 'release_attachment', label: 'Release attachment to outcome' }
  ],
  affirmations: [
    { key: 'i_am', label: '"I am" statements' },
    { key: 'gratitude', label: 'Gratitude affirmations' },
    { key: 'self_compassion', label: 'Self-compassion phrases' },
    { key: 'silent_repetition', label: 'Silent repetition' },
    { key: 'repeat_after_me', label: 'Repeat-after-me phrasing' }
  ],
  remembrance: [
    { key: 'inner_wisdom', label: 'Inner-wisdom inquiry' },
    { key: 'universe_connection', label: 'Connection to the universe' },
    { key: 'observing_observer', label: 'Observing the observer' },
    { key: 'heart_centered', label: 'Heart-centered awareness' },
    { key: 'true_nature', label: 'Remembering true nature' }
  ],
  healing: [
    { key: 'healing_light', label: 'Healing light visualization' },
    { key: 'metta', label: 'Compassion toward self (metta)' },
    { key: 'breathe_tension', label: 'Breathe into areas of tension' },
    { key: 'release_emotion', label: 'Release stored emotion' },
    { key: 'gratitude_body', label: 'Gratitude toward the body' }
  ],
  suggestions: [
    { key: 'calm_anchor', label: 'Calm anchor word' },
    { key: 'better_sleep', label: 'Deeper, easier sleep' },
    { key: 'confidence', label: 'Confidence & self-worth' },
    { key: 'stress_resilience', label: 'Stress resilience' },
    { key: 'return_calm_cue', label: 'Return-to-calm cue' }
  ],
  awakening: [
    { key: 'countdown', label: 'Gradual countdown' },
    { key: 'reorient_sounds', label: 'Reorient to sounds in the room' },
    { key: 'move_fingers_toes', label: 'Gently move fingers & toes' },
    { key: 'energizing_breath', label: 'Deep energizing breath' },
    { key: 'carry_calm', label: 'Carry the calm forward' }
  ],
  reflection: [
    { key: 'notice_arose', label: 'Notice what arose' },
    { key: 'gratitude_moment', label: 'Moment of gratitude' },
    { key: 'daily_intention', label: 'Set a daily intention' },
    { key: 'journaling', label: 'Journaling suggestion' },
    { key: 'acknowledge_effort', label: 'Acknowledge your effort' }
  ]
};

export const ZEN_AMBIENCE_OPTIONS = ['auto', 'none', 'beach', 'waterfall', 'rain', 'river', 'other'] as const;
export const ZEN_MUSIC_OPTIONS = ['auto', 'none', 'ambient', 'piano', 'flute', 'harp', 'violin', 'other'] as const;
export const ZEN_BRAINWAVE_OPTIONS = ['auto', 'none', 'restoration', 'dream', 'twilight', 'relaxation', 'focus', 'alertness', 'learning'] as const;
export const ZEN_WHITE_NOISE_OPTIONS = ['auto', 'none', 'brown', 'pink', 'white'] as const;
